import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "../data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/service-area", label: "Service Area" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold tracking-wide transition-colors duration-200 ${
      isActive ? "text-[#C68B4E]" : "text-[#2B2B2B] hover:text-[#C68B4E]"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-[#2B2B2B]/10 transition-all duration-300 ${
        scrolled ? "bg-[#F7F3EC]/95 backdrop-blur-md shadow-sm" : "bg-[#F7F3EC]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" aria-label="Bright Oak Renovations home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={linkClass}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
              className="flex items-center gap-2 text-sm font-semibold text-[#2B2B2B] hover:text-[#C68B4E] transition-colors"
              data-testid="header-phone-link"
            >
              <Phone size={16} strokeWidth={1.5} />
              {CONTACT.phone}
            </a>
            <Link
              to="/contact"
              data-testid="header-quote-button"
              className="bg-[#C68B4E] text-white hover:bg-[#B37A3D] transition-colors px-6 py-3 text-sm font-bold tracking-wider uppercase"
            >
              Get a Free Quote
            </Link>
          </div>

          <button
            className="lg:hidden text-[#2B2B2B] p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F7F3EC] border-t border-[#2B2B2B]/10 animate-fade-in" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={linkClass}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-quote-button"
              className="bg-[#C68B4E] text-white text-center px-6 py-3 text-sm font-bold tracking-wider uppercase mt-2"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
